import type { TupleToUnion } from 'type-fest'

import { cn } from '@mntn-dev/ui-utilities'

export const inputSizeNames = ['md', 'lg'] as const
export type InputSize = TupleToUnion<typeof inputSizeNames>

const inputSizeClassNameMap: Record<
  InputSize,
  (hasLeftIcon?: boolean, hasRightIcon?: boolean, multiline?: boolean) => string
> = {
  md: (hasLeftIcon, hasRightIcon, multiline) =>
    cn(
      'min-h-10 h-10 px-3 text-sm',
      hasLeftIcon && 'pl-11',
      hasRightIcon && 'pr-11',
      multiline && 'py-3 h-auto'
    ),
  lg: (hasLeftIcon, hasRightIcon, multiline) =>
    cn(
      'min-h-12 h-12 px-4 text-base',
      hasLeftIcon && 'pl-12',
      hasRightIcon && 'pr-12',
      multiline && 'py-4'
    ),
}

const getInputSizeClassName = (
  size: InputSize,
  hasLeftIcon?: boolean,
  hasRightIcon?: boolean,
  multiline?: boolean
) => inputSizeClassNameMap[size](hasLeftIcon, hasRightIcon, multiline)

export type GetInputContainerStylesProps = {
  disabled?: boolean
  readOnly?: boolean
  hasError?: boolean
  hasSuccess?: boolean
  hasWarning?: boolean
}

export type GetInputStylesProps = {
  size: InputSize
  hasLeftIcon?: boolean
  hasRightIcon?: boolean
  multiline?: boolean
}

export const getInputContainerClassName = ({
  disabled,
  hasError,
  hasSuccess,
  hasWarning,
  readOnly,
}: GetInputContainerStylesProps) => {
  const borderOverride = hasError || hasSuccess || hasWarning || readOnly
  return cn('input-container', {
    'grad-border-bottom-underlight-subtle': !readOnly,
    'focus-within:before:border-input-focus': !borderOverride,
    'before:border-negative before:hover:border-negative-hover before:active:border-negative-press':
      hasError,
    'before:border-positive before:hover:border-positive-hover before:active:border-positive-press':
      hasSuccess,
    'before:border-caution before:hover:border-caution-hover before:active:border-caution-press':
      hasWarning,
    'bg-input-disabled hover:bg-input-disabled active:bg-input-disabled':
      disabled,
    'grad-border-transparent hover:bg-input active:bg-input': readOnly,
  })
}

export const getInputStyles = ({
  size,
  hasLeftIcon,
  hasRightIcon,
  multiline,
}: GetInputStylesProps) => {
  return cn(
    'input-base',
    getInputSizeClassName(size, hasLeftIcon, hasRightIcon, multiline)
  )
}

export const getInputIconClassName = (
  iconPosition: 'left' | 'right',
  interactive?: boolean
) =>
  cn(
    'absolute h-5 flex justify-center items-center shrink-0',
    { 'right-3': iconPosition === 'right', 'left-3': iconPosition === 'left' },
    !interactive && 'pointer-events-none'
  )
